import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs } from 'antd';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider } from 'antd';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import {
  fetchOrdersByType,
  fetchOrdersHistory,
  setSelectedDateRange,
} from '../../redux/ordersSlice';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import type { AppDispatch, RootState } from '../../redux/store';
import type { OrderTabType } from '../../redux/ordersSlice';
import OrderStatsSection from './OrderStatsSection';
import OrderFiltersSection from './OrderFiltersSection';
import OrderTable from './OrderTable';
import TablePagination from '../../commonComponents/Pagination';
import { Container, HeaderSection, Title, Subtitle, TabsCard, RefreshButton, OrdersStatusDropdownStyle } from './styles';
import { ORDER_TABS, TAB_LABELS, TAB_ICONS, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from './constants';

const Orders: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const {
    orders,
    totalPages,
    totalCount,
    selectedDateRange,
    tabCounts,
    loading,
  } = useSelector((state: RootState) => state.orders);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [selectedTab, setSelectedTabLocal] = useState<OrderTabType>('pending');
  const [selectedFilter, setSelectedFilterLocal] = useState('');
  const [expandedOrderId, setExpandedOrderIdLocal] = useState<string | null>(null);

  const fetchOrders = useCallback(() => {
    let startDate = '';
    let endDate = '';

    if (selectedDateRange?.startDate && typeof selectedDateRange.startDate === 'string') {
      startDate = selectedDateRange.startDate.trim();
    }
    if (selectedDateRange?.endDate && typeof selectedDateRange.endDate === 'string') {
      endDate = selectedDateRange.endDate.trim();
    }

    if (selectedTab === 'history') {
      dispatch(
        fetchOrdersHistory({
          startDate: startDate,
          endDate: endDate,
          page: currentPage,
          limit,
        })
      );
    } else {
      dispatch(fetchOrdersByType({
        status: selectedTab as OrderTabType,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }));
    }
  }, [
    dispatch,
    selectedTab,
    selectedDateRange?.startDate,
    selectedDateRange?.endDate,
    currentPage,
    limit,
  ]);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, selectedDateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedTab === 'history') {
      fetchOrders();
    }
  }, [currentPage, limit, selectedTab, fetchOrders]);

  const handleDateRangeChange = useCallback(
    (range: { startDate: moment.Moment | Date | null; endDate: moment.Moment | Date | null }) => {
      const start = range.startDate
        ? moment.isMoment(range.startDate)
          ? range.startDate.format('YYYY-MM-DD')
          : moment(range.startDate as Date).format('YYYY-MM-DD')
        : '';
      const end = range.endDate
        ? moment.isMoment(range.endDate)
          ? range.endDate.format('YYYY-MM-DD')
          : moment(range.endDate as Date).format('YYYY-MM-DD')
        : '';
      setCurrentPage(1); // Reset to first page when date changes
      dispatch(setSelectedDateRange({ startDate: start, endDate: end }));
    },
    [dispatch]
  );

  const handleDateSelect = useCallback(
    (range: { startDate: Date | null; endDate: Date | null }) => {
      const start = range?.startDate ? moment(range?.startDate).format('YYYY-MM-DD') : '';
      const end = range?.endDate ? moment(range?.endDate).format('YYYY-MM-DD') : '';
      setCurrentPage(1); // Reset to first page when date changes
      dispatch(setSelectedDateRange({ startDate: start, endDate: end }));
    },
    [dispatch]
  );

  const selectedDateRangeForPicker = useMemo(() => {
    if (!selectedDateRange?.startDate || !selectedDateRange?.endDate) {
      return { startDate: null, endDate: null };
    }
    return {
      startDate: moment(selectedDateRange?.startDate),
      endDate: moment(selectedDateRange?.endDate),
    };
  }, [selectedDateRange]);

  const filteredOrders = useMemo(() => {
    let list = orders;
    if (searchTerm?.trim()) {
      const term = searchTerm?.trim()?.toLowerCase();
      list = list?.filter((o) => (o.user_name || '')?.toLowerCase()?.includes(term));
    }
    if (selectedFilter && selectedFilter !== 'all') {
      list = list?.filter((o) => (o.status || '')?.toLowerCase() === selectedFilter?.toLowerCase());
    }
    return list;
  }, [orders, searchTerm, selectedFilter]);

  const totalRevenue = useMemo(
    () => filteredOrders?.reduce((sum, o) => sum + (parseFloat(o?.total_price || '0') || 0), 0),
    [filteredOrders]
  );
  const avgOrderValue = filteredOrders?.length ? totalRevenue / filteredOrders?.length : 0;
  const pendingCount = selectedTab === 'pending' ? filteredOrders?.length : 0;

  const handleTabChange = useCallback(
    (key: string) => {
      setSelectedTabLocal(key as OrderTabType);
      setExpandedOrderIdLocal(null);
      setCurrentPage(1);
    },
    []
  );

  const handlePageChange = useCallback(
    (page: number, pageSize: number) => {
      setCurrentPage(page);
      setLimit(pageSize);
    },
    []
  );

  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };

  return (
    <ThemeProvider theme={theme}>
      <OrdersStatusDropdownStyle />
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <div>
              <Title level={2}>Orders</Title>
              <Subtitle>View and manage orders by status</Subtitle>
            </div>
            <RefreshButton
              type="default"
              icon={<FontAwesomeIcon icon={faSync} spin={loading} />}
              onClick={() => fetchOrders()}
              disabled={loading}
              aria-label="Refresh orders"
            >
              Refresh
            </RefreshButton>
          </HeaderSection>

          <OrderStatsSection
            orders={filteredOrders}
            totalRevenue={totalRevenue}
            avgOrderValue={avgOrderValue}
            pendingCount={pendingCount}
            loading={loading}
          />

          <OrderFiltersSection
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={selectedFilter}
            onStatusFilterChange={setSelectedFilterLocal}
            showHistoryFilters={selectedTab === 'history'}
            selectedDateRange={selectedDateRangeForPicker}
            onDateRangeChange={handleDateRangeChange}
            onDateSelect={handleDateSelect}
          />

          <TabsCard>
            <Tabs
              activeKey={selectedTab}
              onChange={handleTabChange}
              items={ORDER_TABS.map((tab) => ({
                key: tab,
                label: (
                  <span className="orders-tab-label">
                    <FontAwesomeIcon icon={TAB_ICONS[tab]} style={{ marginRight: 8 }} />
                    {TAB_LABELS[tab]}
                    <span className="tab-badge">{tabCounts[tab]}</span>
                  </span>
                ),
                children: (
                  <>
                    <OrderTable
                      orders={filteredOrders}
                      loading={loading}
                      expandedOrderId={expandedOrderId}
                      onToggleExpand={(id) =>
                        setExpandedOrderIdLocal(expandedOrderId === id ? null : id)
                      }
                      searchTerm={searchTerm}
                      statusFilter={selectedFilter}
                      tabName={TAB_LABELS[tab]}
                    />
                    {selectedTab === 'history' && (
                      <TablePagination
                        totalCount={totalCount || totalPages * limit}
                        currentPage={currentPage}
                        pageSize={limit}
                        onChange={handlePageChange}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        defaultPageSize={DEFAULT_PAGE_SIZE}
                      />
                    )}
                  </>
                ),
              }))}
            />
          </TabsCard>
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default Orders;
